import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveyAuditApi } from '../api/surveyAuditApi';
import { surveyAuditQueryKeys } from './queryKeys';
import {
  UpdateSurveyAuditDTO,
  UpdateQuestionInclusionsDTO,
  UpdateCommentInclusionsDTO,
  BulkUpdateInclusionsDTO,
} from '../dtos';
import { UseSurveyAuditOptions } from '../types';
import { useNotifications } from '@shared/components/notifications/useNotifications';
import { AUDIT_EVENTS } from '../../../utils/trackingEvents';
import { usePostHog } from '../../../hooks/usePostHog';

// Hook for getting a single survey audit
export const useSurveyAudit = (auditId: string, options?: UseSurveyAuditOptions) => {
  return useQuery({
    queryKey: surveyAuditQueryKeys.detail(auditId),
    queryFn: () => surveyAuditApi.getSurveyAudit(auditId),
    enabled: options?.enabled !== false && !!auditId,
    refetchInterval: options?.refetchInterval,
    meta: {
      onError: options?.onError,
      onSuccess: options?.onSuccess,
    },
  });
};

// Hook for getting survey audits by survey instance
export const useSurveyAuditsBySurveyInstance = (surveyInstanceId: string, enabled = true) => {
  return useQuery({
    queryKey: surveyAuditQueryKeys.listBySurveyInstance(surveyInstanceId),
    queryFn: () => surveyAuditApi.getSurveyAuditsBySurveyInstance(surveyInstanceId),
    enabled: enabled && !!surveyInstanceId,
  });
};

// Hook for getting survey audits by subject with optional status filter
export const useSurveyAuditsBySubject = (
  subjectId: string,
  options?: {
    status?: string | string[];
    limit?: number;
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: surveyAuditQueryKeys.listBySubject(subjectId, options?.status),
    queryFn: () =>
      surveyAuditApi.getSurveyAuditsBySubject(subjectId, {
        status: options?.status,
        limit: options?.limit,
      }),
    enabled: options?.enabled !== false && !!subjectId,
  });
};

// Hook for starting a new audit
export const useStartAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: ({
      surveyInstanceId,
      userRole,
      surveyId,
    }: {
      surveyInstanceId: string;
      userRole: 'PROFESSOR' | 'COURSE_REPRESENTATIVE';
      surveyId: string;
    }) => surveyAuditApi.startAudit(surveyInstanceId, userRole, surveyId),
    onSuccess: (response, variables) => {
      // Track audit start
      captureEvent(AUDIT_EVENTS.AUDIT_STARTED, {
        audit_id: response.data?.id,
        survey_instance_id: variables.surveyInstanceId,
        survey_id: response.data?.surveyId,
        audited_by: response.data?.auditedBy,
        tenant_id: response.data?.tenantId,
        domain_id: response.data?.domainId,
        subject_id: response.data?.subjectId,
        started_at: response.data?.startedAt,
      });

      // Invalidate all audit-related queries to refresh tables
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit started successfully');
    },
    onError: () => {
      error('Failed to start audit');
    },
  });
};

// Hook for updating survey audit
export const useUpdateSurveyAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: UpdateSurveyAuditDTO }) =>
      surveyAuditApi.updateSurveyAudit(auditId, data),
    onSuccess: (response, variables) => {
      // Update the specific audit in cache
      queryClient.setQueryData(surveyAuditQueryKeys.detail(variables.auditId), response);
      // Invalidate related list queries
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit updated successfully');
    },
    onError: () => {
      error('Failed to update audit');
    },
  });
};

// Hook for updating question inclusions
export const useUpdateQuestionInclusions = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({
      auditId,
      questionId,
      data,
    }: {
      auditId: string;
      questionId: string;
      data: UpdateQuestionInclusionsDTO;
    }) => surveyAuditApi.updateQuestionInclusions(auditId, questionId, data),
    onSuccess: (response, variables) => {
      // Update the audit in cache
      queryClient.setQueryData(surveyAuditQueryKeys.detail(variables.auditId), response);
      // Invalidate progress query to get updated statistics
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.progress(variables.auditId),
      });
      success('Question inclusions updated');
    },
    onError: () => {
      error('Failed to update question inclusions');
    },
  });
};

// Hook for updating comment inclusions
export const useUpdateCommentInclusions = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({
      auditId,
      questionId,
      data,
    }: {
      auditId: string;
      questionId: string;
      data: UpdateCommentInclusionsDTO;
    }) => surveyAuditApi.updateCommentInclusions(auditId, questionId, data),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(surveyAuditQueryKeys.detail(variables.auditId), response);
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.progress(variables.auditId),
      });
      success('Comment inclusions updated');
    },
    onError: () => {
      error('Failed to update comment inclusions');
    },
  });
};

// Hook for bulk updating inclusions
export const useBulkUpdateInclusions = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({ auditId, data }: { auditId: string; data: BulkUpdateInclusionsDTO }) =>
      surveyAuditApi.bulkUpdateInclusions(auditId, data),
    onSuccess: (response, variables) => {
      queryClient.setQueryData(surveyAuditQueryKeys.detail(variables.auditId), response);
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.progress(variables.auditId),
      });
      success('Bulk update completed');
    },
    onError: () => {
      error('Failed to perform bulk update');
    },
  });
};

// Hook for updating single response inclusion atomically (response + comment)
export const useUpdateResponseInclusion = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: ({
      auditId,
      questionId,
      responseId,
      responseIncluded,
      commentIncluded,
      hasComment,
    }: {
      auditId: string;
      questionId: string;
      responseId: string;
      responseIncluded: boolean;
      commentIncluded: boolean;
      hasComment: boolean;
    }) => {
      // Use bulk update endpoint for atomic operation
      const bulkUpdate: BulkUpdateInclusionsDTO = {
        updates: [
          {
            questionId,
            responseInclusions: {
              included: responseIncluded ? [responseId] : [],
              excluded: responseIncluded ? [] : [responseId],
            },
            ...(hasComment && {
              commentInclusions: {
                included: commentIncluded ? [responseId] : [],
                excluded: commentIncluded ? [] : [responseId],
              },
            }),
          },
        ],
      };

      return surveyAuditApi.bulkUpdateInclusions(auditId, bulkUpdate);
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData(surveyAuditQueryKeys.detail(variables.auditId), response);
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.progress(variables.auditId),
      });
      success('Response updated');
    },
    onError: () => {
      error('Failed to update response');
    },
  });
};

// Hook for completing audit
export const useCompleteAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: (auditId: string) => surveyAuditApi.completeAudit(auditId),
    onSuccess: (response, auditId) => {
      // Get existing audit data from cache for tracking
      const existingAuditData = queryClient.getQueryData(
        surveyAuditQueryKeys.detail(auditId)
      ) as any;
      const audit = existingAuditData?.data;

      // Track audit completion
      captureEvent(AUDIT_EVENTS.AUDIT_COMPLETED, {
        audit_id: auditId,
        survey_id: audit?.surveyId,
        survey_instance_id: audit?.surveyInstanceId,
        audited_by: audit?.auditedBy,
        started_at: audit?.startedAt,
        completed_at: response.data?.completedAt,
        tenant_id: audit?.tenantId,
        domain_id: audit?.domainId,
        subject_id: audit?.subjectId,
        questions_reviewed:
          response.data?.finalQuestionInclusions?.length || audit?.questionInclusions?.length || 0,
        total_responses_included:
          response.data?.finalQuestionInclusions?.reduce(
            (total: number, q: any) => total + (q.includedResponseIds?.length || 0),
            0
          ) || 0,
        total_responses_excluded:
          response.data?.finalQuestionInclusions?.reduce(
            (total: number, q: any) => total + (q.excludedResponseIds?.length || 0),
            0
          ) || 0,
        is_locked: true,
      });

      // Invalidate all audit-related queries
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audit(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit completed successfully');
    },
    onError: () => {
      error('Failed to complete audit');
    },
  });
};

// Hook for publishing audit
export const usePublishAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: (auditId: string) => surveyAuditApi.publishAudit(auditId),
    onSuccess: (response, auditId) => {
      // Get existing audit data from cache for tracking
      const existingAuditData = queryClient.getQueryData(
        surveyAuditQueryKeys.detail(auditId)
      ) as any;
      const audit = existingAuditData?.data;

      // Track audit publishing
      captureEvent(AUDIT_EVENTS.AUDIT_PUBLISHED, {
        audit_id: auditId,
        survey_id: audit?.surveyId,
        survey_instance_id: audit?.surveyInstanceId,
        audited_by: audit?.auditedBy,
        started_at: audit?.startedAt,
        completed_at: audit?.completedAt,
        published_at: response.data?.publishedAt,
        tenant_id: audit?.tenantId,
        domain_id: audit?.domainId,
        subject_id: audit?.subjectId,
        questions_reviewed: audit?.questionInclusions?.length || 0,
        total_responses_included:
          audit?.questionInclusions?.reduce(
            (total: number, q: any) => total + (q.includedResponseIds?.length || 0),
            0
          ) || 0,
        total_responses_excluded:
          audit?.questionInclusions?.reduce(
            (total: number, q: any) => total + (q.excludedResponseIds?.length || 0),
            0
          ) || 0,
        is_locked: true,
        can_generate_report: true,
      });

      // Invalidate all audit-related queries
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audit(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit published successfully');
    },
    onError: () => {
      error('Failed to publish audit');
    },
  });
};

// Hook for archiving audit
export const useArchiveAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: ({ auditId, subjectId }: { auditId: string; subjectId: string }) =>
      surveyAuditApi.archiveAudit(auditId, subjectId),
    onSuccess: (response, { auditId }) => {
      // Get existing audit data from cache for tracking
      const existingAuditData = queryClient.getQueryData(
        surveyAuditQueryKeys.detail(auditId)
      ) as any;
      const audit = existingAuditData?.data;

      // Track audit archiving
      captureEvent(AUDIT_EVENTS.AUDIT_ARCHIVED, {
        audit_id: auditId,
        survey_id: audit?.surveyId,
        survey_instance_id: audit?.surveyInstanceId,
        tenant_id: audit?.tenantId,
        domain_id: audit?.domainId,
        subject_id: audit?.subjectId,
        previous_status: audit?.status,
        new_status: 'ARCHIVED',
      });

      // Invalidate all audit-related queries
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audit(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit archived successfully');
    },
    onError: () => {
      error('Failed to archive audit');
    },
  });
};

// Hook for unarchiving audit
export const useUnarchiveAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: ({ auditId, subjectId }: { auditId: string; subjectId: string }) =>
      surveyAuditApi.unarchiveAudit(auditId, subjectId),
    onSuccess: (response, { auditId }) => {
      // Get existing audit data from cache for tracking
      const existingAuditData = queryClient.getQueryData(
        surveyAuditQueryKeys.detail(auditId)
      ) as any;
      const audit = existingAuditData?.data;

      // Track audit unarchiving
      captureEvent(AUDIT_EVENTS.AUDIT_UNARCHIVED, {
        audit_id: auditId,
        survey_id: audit?.surveyId,
        survey_instance_id: audit?.surveyInstanceId,
        tenant_id: audit?.tenantId,
        domain_id: audit?.domainId,
        subject_id: audit?.subjectId,
        previous_status: 'ARCHIVED',
        new_status: response.data?.status || 'READY',
      });

      // Invalidate all audit-related queries
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audit(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit unarchived successfully');
    },
    onError: () => {
      error('Failed to unarchive audit');
    },
  });
};

// Hook for completing and publishing audit in one action
export const useCompleteAndPublishAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();
  const { captureEvent } = usePostHog();

  return useMutation({
    mutationFn: async (auditId: string) => {
      // First complete the audit
      const completeResponse = await surveyAuditApi.completeAudit(auditId);
      // Then publish it
      const publishResponse = await surveyAuditApi.publishAudit(auditId);
      return { completeResponse, publishResponse };
    },
    onSuccess: ({ completeResponse, publishResponse }, auditId) => {
      // Get existing audit data from cache for tracking
      const existingAuditData = queryClient.getQueryData(
        surveyAuditQueryKeys.detail(auditId)
      ) as any;
      const audit = existingAuditData?.data;

      // Track audit completion
      captureEvent(AUDIT_EVENTS.AUDIT_COMPLETED, {
        audit_id: auditId,
        survey_id: audit?.surveyId,
        survey_instance_id: audit?.surveyInstanceId,
        audited_by: audit?.auditedBy,
        started_at: audit?.startedAt,
        completed_at: completeResponse.data?.completedAt,
        tenant_id: audit?.tenantId,
        domain_id: audit?.domainId,
        subject_id: audit?.subjectId,
        questions_reviewed:
          completeResponse.data?.finalQuestionInclusions?.length ||
          audit?.questionInclusions?.length ||
          0,
        total_responses_included:
          completeResponse.data?.finalQuestionInclusions?.reduce(
            (total: number, q: any) => total + (q.includedResponseIds?.length || 0),
            0
          ) || 0,
        total_responses_excluded:
          completeResponse.data?.finalQuestionInclusions?.reduce(
            (total: number, q: any) => total + (q.excludedResponseIds?.length || 0),
            0
          ) || 0,
        is_locked: true,
      });

      // Track audit publishing
      captureEvent(AUDIT_EVENTS.AUDIT_PUBLISHED, {
        audit_id: auditId,
        survey_id: audit?.surveyId,
        survey_instance_id: audit?.surveyInstanceId,
        audited_by: audit?.auditedBy,
        started_at: audit?.startedAt,
        completed_at: completeResponse.data?.completedAt,
        published_at: publishResponse.data?.publishedAt,
        tenant_id: audit?.tenantId,
        domain_id: audit?.domainId,
        subject_id: audit?.subjectId,
        questions_reviewed:
          completeResponse.data?.finalQuestionInclusions?.length ||
          audit?.questionInclusions?.length ||
          0,
        total_responses_included:
          completeResponse.data?.finalQuestionInclusions?.reduce(
            (total: number, q: any) => total + (q.includedResponseIds?.length || 0),
            0
          ) || 0,
        total_responses_excluded:
          completeResponse.data?.finalQuestionInclusions?.reduce(
            (total: number, q: any) => total + (q.excludedResponseIds?.length || 0),
            0
          ) || 0,
        is_locked: true,
        can_generate_report: true,
      });

      // Invalidate all audit-related queries
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audit(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit published successfully');
    },
    onError: (err) => {
      console.error('Failed to complete and publish audit:', err);
      error('Failed to publish audit');
    },
  });
};

// Hook for deleting survey audit
export const useDeleteSurveyAudit = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotifications();

  return useMutation({
    mutationFn: (auditId: string) => surveyAuditApi.deleteSurveyAudit(auditId),
    onSuccess: (_, auditId) => {
      // Remove from cache and invalidate list queries
      queryClient.removeQueries({
        queryKey: surveyAuditQueryKeys.audit(auditId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyAuditQueryKeys.audits(),
      });
      success('Audit deleted successfully');
    },
    onError: () => {
      error('Failed to delete audit');
    },
  });
};
